import React, { lazy, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Table } from 'antd';
import { APPLICATION_TEMPLATE_COLUMNS } from './columns';
import { Button, Select, Spinner, Layout } from '@iba-team/portal-react-components/dist';
import './ApplicationTemplatesStyles.scss';
import {ReactComponent as PlusIcon} from '@iba-team/portal-react-components/dist/svg/outline/plus.svg';
import { buildModal } from '../../containers/MainWrapper/modal/ModalBuilder';
import { Trans, useTranslation } from 'react-i18next';
import { ApplicationTemplatesGrid, ApplicationTemplatesStore, CountryList } from '../../store/ApplicationTemplates';
import { DeleteApplicationTemplateModalI } from './DeleteApplicationTemplateModal';
import { useEffectOnce, useLocalStorage } from 'react-use';
import { useAnalyticsEventTracker } from '../../analytics/analytics';
import {ReactComponent as InfoIcon } from '@iba-team/portal-react-components/dist/svg/outline/information.svg';
const ApplicationTemplatesCreationModal =  lazy(()=> import('./ApplicationTemplatesCreationModal'));
const DeleteApplicationTemplateModal = lazy(() => import('./DeleteApplicationTemplateModal'));

interface ModalProps{
  currentTemplate: ApplicationTemplatesGrid | null,
  tableData: ApplicationTemplatesGrid[],
}

export const deleteApplicationTemplateModalCall =
  buildModal<undefined, Omit<DeleteApplicationTemplateModalI, 'onClose'>>((props, onClose) =>
    <DeleteApplicationTemplateModal currentLocation={props.currentLocation} onClose={onClose} templateData={props.templateData}/>);
export const applicationTemplateModalCall =
  buildModal<undefined, ModalProps>((props, onClose)=>
    <ApplicationTemplatesCreationModal onClose={onClose} templateData={props.currentTemplate} tableData={props.tableData}/>);

export const RecommendationBlock = () => {
  return <div className={'confluenceLink'} >
    <InfoIcon/>
    <Trans i18nKey={'applicationTemplates.page.title'}> <a target='_blank' href={'https://confluence.ibagroup.eu/display/LMS/Application+Templates+adding'} rel="noreferrer">here</a>.</Trans>
  </div>;
};

export const ApplicationTemplatesPage = observer(() => {


  const {fetchCountryList, dataGrid, fetchDataGrid, countryList, fetchTypesByCountry, convertedTypes, downloadTemplateFile, isTableUpdating} = ApplicationTemplatesStore;
  const {t} = useTranslation();
  const [storageCountry, setStorageCountry] = useLocalStorage('current_location_for_templates' , '');
  const gaEventTracker = useAnalyticsEventTracker('Application_templates');
  const [selectedCountry, setSelectedCountry] = useState<CountryList>(storageCountry ? JSON.parse(storageCountry) : countryList[0]);
  const [isNewPage, setNewPage] = useState<boolean>(false);

  useEffect(()=> {
    fetchCountryList();
  }, []);

  useEffect(()=>{
    !storageCountry && setStorageCountry(JSON.stringify(countryList[0]));
  },[countryList]);

  useEffect(() => {
    if(storageCountry){
      setSelectedCountry(JSON.parse(storageCountry));
    }
  },[storageCountry]);

  useEffect(()=> {
    if(selectedCountry) {
      const countryName = selectedCountry.name;
      if(countryName){
        fetchTypesByCountry(countryName);
        fetchDataGrid(countryName);
      }
    }
  }, [selectedCountry]);

  useEffectOnce(()=> {
    if(selectedCountry && storageCountry){
      const parsed = JSON.parse(storageCountry);
      setSelectedCountry(parsed as unknown as CountryList);
    }
  });

  useEffect(()=> {
    isNewPage && setStorageCountry(JSON.stringify(selectedCountry));
  },[selectedCountry, storageCountry]);

  applicationTemplateModalCall.useIt();
  deleteApplicationTemplateModalCall.useIt();

  const renderHeader = () => {
    return {
      layoutName: <div style={{display: 'flex', alignItems: 'center'}}>
        <h2>
          {t('applicationTemplates.page.page_title')}
        </h2>
        {countryList.length > 0 &&
          <Select
            value={storageCountry ? JSON.parse(storageCountry).name : selectedCountry?.name || ''}
            className={'application_templates_select'}
            render={(item) => <div>{item.name}</div>}
            onChangeValue={(v) => {
              setNewPage(true);
              setSelectedCountry(v);
            }}
            list={countryList}
            disabled={countryList.length < 2}
            uniqueElement={i => i.code === selectedCountry.code}/>
        }
      </div>,
      right:   <div className={'templates__header__info'}>
        <RecommendationBlock/>
        <Button icon={<PlusIcon/>} onClick={()=> {
          gaEventTracker('open_add_template_modal');
          applicationTemplateModalCall.showImmediately({tableData: dataGrid, currentTemplate: null});
        }}
        theme={{color: 'blue', variant: 'secondary'}}>
          {t('applicationTemplates.page.addTemplate')}</Button>
      </div>
    };
  };

  return(
    <Layout className={'templates'} header={renderHeader()}>
      <Table pagination={false}
        loading={{indicator: <Spinner className={'spinner'}/>, spinning: isTableUpdating}}
        className={'templates__table'}
        columns={APPLICATION_TEMPLATE_COLUMNS(convertedTypes, dataGrid, selectedCountry?.name, downloadTemplateFile)}
        dataSource={dataGrid}/>
    </Layout>
  );
});
